import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import type { PublicImage } from "@/pages/Explore/types";
import { retryOnTransientNetwork } from "@/lib/api/fetchWithRetry";

/**
 * Fetch the signed-in user's generated images. Wrapped in a one-shot transient
 * retry: a single `TypeError: Failed to fetch` (tab waking from background, a
 * brief network blip) self-heals instead of surfacing as Sentry noise via the
 * GeneratedImagesContext capture. A sustained outage (retry also fails) still
 * throws so it stays alertable (Sentry: TODOWITH-AI-FRONTEND-1P).
 */
export async function fetchUserGeneratedImages(userId: string): Promise<Tables<"generated_images">[]> {
  return retryOnTransientNetwork(async () => {
    const { data, error } = await supabase
      .from("generated_images")
      .select("*")
      .eq("user_id", userId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data ?? [];
  });
}

export interface PaginatedResult {
  data: PublicImage[];
  hasMore: boolean;
}

const EXPLORE_COLUMNS = "id, image_url, reference_image_url, prompt, aspect_ratio, resolution, pipeline, model, created_at" as const;

/** Pipelines whose generations must never appear in public Explore/Community
 *  feeds. Personal headshots stay private — gallery only.
 *  Product Creative IS public (auto-shared to the community feed). */
const PRIVATE_PIPELINES = ["profile_photo"] as const;

export async function fetchPublicImagesPaginated(
  pageSize: number,
  cursor?: string,
  excludeUserId?: string,
): Promise<PaginatedResult> {
  let query = supabase
    .from("generated_images")
    .select(EXPLORE_COLUMNS)
    .eq("status", "completed")
    .not("pipeline", "in", `(${PRIVATE_PIPELINES.join(",")})`)
    .order("created_at", { ascending: false })
    .limit(pageSize + 1);

  if (cursor) {
    query = query.lt("created_at", cursor);
  }

  if (excludeUserId) {
    query = query.neq("user_id", excludeUserId);
  }

  const { data, error } = await query;
  if (error) throw error;

  const rows = data ?? [];
  const hasMore = rows.length > pageSize;

  return {
    data: (hasMore ? rows.slice(0, pageSize) : rows) as PublicImage[],
    hasMore,
  };
}

export async function fetchRandomPublicImages(count: number): Promise<PublicImage[]> {
  const { count: total, error: countErr } = await supabase
    .from("generated_images")
    .select("id", { count: "exact", head: true })
    .eq("status", "completed")
    .not("pipeline", "in", `(${PRIVATE_PIPELINES.join(",")})`);

  if (countErr) throw countErr;

  const safeTotal = total ?? 0;
  if (safeTotal === 0) return [];

  const maxOffset = Math.max(0, safeTotal - count);
  const offset = maxOffset > 0 ? Math.floor(Math.random() * maxOffset) : 0;

  const { data, error } = await supabase
    .from("generated_images")
    .select(EXPLORE_COLUMNS)
    .eq("status", "completed")
    .not("pipeline", "in", `(${PRIVATE_PIPELINES.join(",")})`)
    .order("created_at", { ascending: false })
    .range(offset, offset + count - 1);

  if (error) throw error;
  return (data ?? []) as PublicImage[];
}

export async function deleteGeneratedImageById(imageId: string): Promise<void> {
  const { error } = await supabase
    .from("generated_images")
    .delete()
    .eq("id", imageId);

  if (error) throw error;
}

export async function deleteGeneratedImagesByIds(imageIds: string[]): Promise<void> {
  if (imageIds.length === 0) return;
  const { error } = await supabase
    .from("generated_images")
    .delete()
    .in("id", imageIds);
  if (error) throw error;
}

export async function deleteGeneratedImageByUrl(imageUrl: string): Promise<void> {
  const { error } = await supabase
    .from("generated_images")
    .delete()
    .eq("image_url", imageUrl);

  if (error) throw error;
}
